import { useEffect, useState } from "react";
import { Button, Container, Icon, Loading, Modal, Panel, SearchInput, Spinner, Typography } from "@/components/atoms";
import { LoadingSplash } from "@/components/molecules";
import { GeneralFooter, GeneralHeader } from "@/components/organisms";
import { OperatorEmblem } from "@/components/organisms/SignUp/contents";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useSiteConfig } from "@/hooks";
import { useAttachClientToProviderMutation } from "@/redux/apis/account/accountApi";
import { useLazyVerifyProviderQuery } from "@/redux/apis/auth/authApi";
import { useGetClientOperatorsQuery } from "@/redux/apis/operator/operatorApi";
import { useAppSelector } from "@/redux/hooks";
import { Operator } from "@/redux/slices/operator/types";
import { profileSelector } from "@/redux/slices/profile/selectors";
import { addToast } from "@/utils";
import { Restricted } from "../Restricted/Restricted";

// Mirror App.tsx basename normalization so the post-join redirect lands on the correct absolute path
// under the app's subpath (e.g. /dashboard/client).
const rawBase = import.meta.env.VITE_RIDEMINDER_BASE_NAME || "";
const basename = rawBase.replace(/^\/|\/$/g, "");
const basePrefix = basename ? `/${basename}` : "";

type JoinStatus = "idle" | "connecting" | "success";

export const JoinOperator = () => {
  const { apiUrl } = useSiteConfig();
  const profile = useAppSelector(profileSelector);
  const activeOperator = useAppSelector((state) => state.operator.activeOperator);
  const [attachClientToProvider] = useAttachClientToProviderMutation();
  const { isFetching: isFetchingOperators, data } = useGetClientOperatorsQuery();
  const [searchProvider, { isFetching: isSearchingProvider }] = useLazyVerifyProviderQuery();
  const [isProviderInvalid, setIsProviderInvalid] = useState<boolean>(false);
  const [selectedOperator, setSelectedOperator] = useState<Operator>();
  const [search, setSearch] = useState<string>();
  const [status, setStatus] = useState<JoinStatus>("idle");
  const isLoading = isFetchingOperators || status === "connecting";

  useEffect(() => {
    if (search) {
      searchProvider({ providerCode: search, apiUrl })
        .unwrap()
        .then((operator) => {
          setIsProviderInvalid(false);
          setSelectedOperator(operator);
        })
        .catch(() => {
          setIsProviderInvalid(true);
          setSelectedOperator(undefined);
        });
    } else {
      setIsProviderInvalid(false);
      setSelectedOperator(undefined);
    }
  }, [search, searchProvider, apiUrl]);

  const onSubmit = () => {
    if (!selectedOperator) return;

    if (data?.data.operators.find((operator) => operator.id === selectedOperator.id)) {
      const { isSuspended } = selectedOperator.flags;
      if (isSuspended) addToast("danger", `${selectedOperator.name} is no longer active on RideMinder.`);
      else addToast("danger", `You've been suspended by ${selectedOperator.name}.`);
      return;
    }

    setStatus("connecting");
    attachClientToProvider({ clientId: profile.id, providerId: selectedOperator.id })
      .unwrap()
      .then(() => setStatus("success"))
      .catch((e) => {
        setStatus("idle");
        getErrorMessages(e).forEach((message) => addToast("danger", message));
      });
  };

  // Full reload (not react-router navigate) — re-runs useSetupClientAccount against the newly joined
  // operator so branding/config/account state refresh for the new tenant.
  const goToDashboard = () => window.location.replace(`${basePrefix}/client`);

  const renderProvider = () => {
    if (isSearchingProvider) {
      return (
        <div className="flex flex-col items-center justify-center gap-y-4 px-7 pb-[30px] pt-8 md:px-14 md:pt-[60px]">
          <Spinner className="h-12 w-12" />
          <Typography variant="paragraph">Checking Provider Code...</Typography>
        </div>
      );
    }

    if (isProviderInvalid) {
      return (
        <div className="flex flex-col items-center justify-center gap-y-4 px-7 pb-[30px] pt-8 md:px-14 md:pt-[60px]">
          <Icon variant="Bold" name="Danger" className="text-warning" />
          <Typography variant="paragraph" className="text-center">
            We couldn&apos;t find the Transport Provider Code that you&apos;ve entered. Please contact your provider
          </Typography>
        </div>
      );
    } else if (selectedOperator) {
      return (
        <div className="flex flex-col items-center justify-center gap-y-4 px-7 pb-[30px] pt-8 md:px-14 md:pt-[60px]">
          <OperatorEmblem name={selectedOperator.name} emblem={selectedOperator.images.emblem} />
          <Typography variant="action" className="text-center">
            {selectedOperator.name}
          </Typography>
        </div>
      );
    }
  };

  // Full-screen dark splash, shared with the create-booking wait (GuestBookingSplash) so both look identical.
  const renderConnecting = () => (
    <LoadingSplash
      headingId="join-connecting-heading"
      title={`Connecting you to ${selectedOperator?.name}…`}
      subtitle="Hang tight while we set things up."
      statusLabel="Connecting to operator"
    />
  );

  // Success confirmation modal (Figma 5930-34670).
  const renderSuccess = () => (
    <Modal open variant="web-small" onClose={goToDashboard}>
      <div className="flex flex-col items-center gap-5 text-center">
        {selectedOperator && <OperatorEmblem name={selectedOperator.name} emblem={selectedOperator.images.emblem} />}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2">
            <Typography variant="h1">You&apos;re connected to {selectedOperator?.name}</Typography>
            <Icon variant="Bold" name="TickCircle" className="text-success" />
          </div>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            You&apos;re all set. Start booking rides with {selectedOperator?.name} anytime from your dashboard.
          </Typography>
        </div>
        <Button variant="primary" size="lg" className="w-full" onClick={goToDashboard}>
          Go to Dashboard
        </Button>
      </div>
    </Modal>
  );

  const renderForm = () => (
    <>
      <Panel className="relative flex flex-col">
        {isLoading && <Loading />}
        <Typography variant="h1">Sign up</Typography>
        <Typography variant="paragraph" className="mt-2">
          Join your preferred operator
        </Typography>
        <Typography variant="h3" className="mt-4">
          Transport Provider Code
        </Typography>
        <SearchInput
          className="mt-4"
          search={search}
          onSearch={(value) => setSearch(value)}
          placeholder="Enter your Transport Provider Code"
          isLoading={isSearchingProvider}
        />
        <Typography variant="small" className="mt-1 text-neutral-dark-gray">
          Use the transport provider code to connect with a specific operator. Simply ask the operator for the code.
        </Typography>
        {renderProvider()}
      </Panel>
      <div className="mt-6 flex justify-end">
        <Button variant="primary" endIcon="ArrowRight" disabled={!selectedOperator || isLoading} onClick={onSubmit} size="lg">
          Join Operator
        </Button>
      </div>
    </>
  );

  const renderContent = () => {
    if (status === "success") return renderSuccess();
    if (status === "connecting") return renderConnecting();
    return renderForm();
  };

  if (activeOperator) return <Restricted />;

  return (
    <div className="flex min-h-screen flex-row overflow-hidden">
      <div className="relative flex flex-1 flex-col">
        <GeneralHeader action="logout" />
        <Container className="flex w-full max-w-[1040px] flex-1 flex-col items-center gap-5 px-5 py-8 md:py-[100px]">
          <div className="flex w-full max-w-[510px] flex-1 flex-col">{renderContent()}</div>
          <div className="flex max-w-[510px] flex-row text-center">
            <Typography variant="small" className="text-neutral-dark-gray">
              Contact RideMinder on support@rideminder.com to be matched with a professional transport provider in your state
            </Typography>
          </div>
        </Container>
        <GeneralFooter />
      </div>
    </div>
  );
};
